import { If, VLazyLoadMultiSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { useEmojiContext, useSelectedAccountId } from '@vision/ui/hooks';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import {
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
} from '@vision/ui/services';
import { getBodyText, isObjectNotEmpty, translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildMatrixQuestionStatementOptions,
  hasSelectedQuestionWithStatements as hasSelectedQuestionWithStatementsInData,
  MatrixFlowQuestionItem,
  ReportsDetailGraphicCompareMatrixQuestionStatementsSelect,
  useReportsDetailGraphicCompareMatrixQuestionStatements,
} from '../ReportsDetailGraphicCompareMatrixQuestionStatementsSelect';
import { ReportsDetailGraphicCompareLayout } from '../ReportsDetailGraphicCompareLayout';
import { ReportsDetailGraphicBaseProps } from '../report-detail-graphic-compare.util';

interface ReportsDetailGraphicCompareQuestionTitleProps extends ReportsDetailGraphicBaseProps {}

export function ReportsDetailGraphicCompareQuestionTitle({
  compareItemType,
  required,
}: ReportsDetailGraphicCompareQuestionTitleProps) {
  const { t, i18n } = useTranslation();
  const { textWithNativeEmoji } = useEmojiContext();
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const [flowQuestions, setFlowQuestions] = useState<MatrixFlowQuestionItem[]>([]);
  const selectedQuestionFlowIds = formik.values.selectedCompareCriteria.selectedQuestionFlowIds;
  const selectedQuestionIds = formik.values.selectedCompareCriteria.selectedQuestionIds;

  const matrixQuestionStatements = useMemo(() => {
    return buildMatrixQuestionStatementOptions({
      selectedQuestionIds,
      flowQuestions,
      language: i18n.language,
      textWithNativeEmoji,
    });
  }, [selectedQuestionIds, flowQuestions, textWithNativeEmoji, i18n.language]);

  const hasSelectedQuestionWithStatements = useMemo(() => {
    return hasSelectedQuestionWithStatementsInData(selectedQuestionIds, flowQuestions);
  }, [selectedQuestionIds, flowQuestions]);

  const { selectedMatrixQuestionStatements, shouldShowMatrixQuestionStatements, handleChangeMatrixQuestionStatements } =
    useReportsDetailGraphicCompareMatrixQuestionStatements({
      formik,
      selectedFlowIds: selectedQuestionFlowIds,
      selectedQuestionIds,
      hasSelectedQuestionWithStatements,
      matrixQuestionStatements,
    });

  const handleDefaultValuesLoaded = (data: SimpleFlowQuestion[]) => {
    const newIds = formik.values.selectedCompareCriteria.selectedQuestionIds.filter((id) =>
      data.some((item) => item.id === id),
    );
    formik.setFieldValue('selectedCompareCriteria.selectedQuestionIds', newIds);
  };

  const handleChangeQuestions = (values: string[]) => {
    formik.setFieldValue('selectedCompareCriteria.selectedQuestionIds', values);
    formik.setFieldValue('selectedCompareCriteria.selectedMatrixQuestionStatementIds', []);
  };

  return (
    <ReportsDetailGraphicCompareLayout title={t(`page-reports:reportCompareGroupItemType.${compareItemType}`)}>
      <VLazyLoadMultiSelect
        id="report-compare-question-title-flow"
        data-testid="input-report-compare-question-title-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{ nodeId: accountId, sortBy: 'name', sortOrder: 'asc' }}
        multiSelectItemMapper={(items) => {
          return items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }}
        searchKey="flow_name"
        value={formik.values.selectedCompareCriteria.selectedQuestionFlowIds}
        searchable={true}
        clearable={true}
        onBlur={formik.handleBlur}
        onChange={(values) => formik.setFieldValue('selectedCompareCriteria.selectedQuestionFlowIds', values)}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionFlowIds')}
        withAsterisk={required}
        itemComponent={VSelectOptionWithDescription}
      />

      <If value={formik.values.selectedCompareCriteria.selectedQuestionFlowIds.length > 0}>
        <VLazyLoadMultiSelect
          id="report-compare-question-title-question"
          data-testid="input-report-compare-question-title-question"
          label={t('question')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            flowIds: formik.values.selectedCompareCriteria.selectedQuestionFlowIds,
            sortBy: 'name',
            sortOrder: 'asc',
          }}
          multiSelectItemMapper={(items) => {
            return items
              .filter((item) => isObjectNotEmpty(item.body))
              .map((item) => ({
                value: item.id,
                label: textWithNativeEmoji(getBodyText(item.body, i18n.language)),
                description: item?.flow_name,
              }));
          }}
          searchKey="question_body"
          searchable={true}
          value={selectedQuestionIds}
          onBlur={formik.handleBlur}
          onChange={handleChangeQuestions}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionIds')}
          resetChangeRequestParameters={true}
          withAsterisk={required}
          onDefaultValuesLoaded={handleDefaultValuesLoaded}
          onDataChange={(data: SimpleFlowQuestion[]) => setFlowQuestions(data as MatrixFlowQuestionItem[])}
          itemComponent={VSelectOptionWithDescription}
        />

        <If value={shouldShowMatrixQuestionStatements}>
          <ReportsDetailGraphicCompareMatrixQuestionStatementsSelect
            id="report-compare-question-title-matrix-question-statements"
            dataTestId="input-report-compare-question-title-matrix-question-statements"
            data={matrixQuestionStatements}
            value={selectedMatrixQuestionStatements}
            onBlur={formik.handleBlur}
            onChange={handleChangeMatrixQuestionStatements}
          />
        </If>
      </If>
    </ReportsDetailGraphicCompareLayout>
  );
}
